<script lang="ts" setup>
import type { Agent } from '~/core/entities/agent/Agent';

import FavoriteFooterButton from '@/components/FavoriteFooterButton/FavoriteFooterButton.vue';
import {
	FavoriteEntranceState,
	useFavoriteEntranceAnimation
} from '@/composables/useFavoriteEntranceAnimation';
import { useFavoriteStarPulse } from '@/composables/useFavoriteStarPulse';
import { useToggleFavoriteSync } from '~/composables/useToggleFavoriteSync';
import { logger } from '~/core/service/logger';
import { cn } from '~/utils';
import { getAgentFirstName } from '~~/utils';

import { BrokerAvatarInfo } from '~/components/BrokerAvatarInfo';
import { GraphicsTriangle } from '~/components/GraphicsTriangle';

import IconMaterialSymbolsLightChatBubbleOutlineSharp from '~icons/material-symbols-light/chat-bubble-outline-sharp';

// Animation constants
const ANIMATION_CONSTANTS = {
	READY_DELAY_MS: 300,
	TRANSITION_DURATION_MS: 2000,
	ENTER_DURATION_MS: 400,
	LEAVE_DURATION_MS: 300
} as const;

const props = withDefaults(
	defineProps<{
		agent?: Agent;
		companyLogo?: string | null;
		companyName?: string;
		isExclusive?: boolean;
		propertyId?: string;
		commercialId?: string;
		inUserWishlist?: boolean;
	}>(),
	{
		companyName: '',
		isExclusive: false,
		propertyId: '',
		commercialId: '',
		inUserWishlist: false
	}
);

const emit = defineEmits<{
	clickFixedFooterContact: [];
}>();

const agentFirstName = computed(() => getAgentFirstName(props.agent));
const ctaText = computed(() => `Falar com ${agentFirstName.value}`);

const route = useRoute();
const eventData = computed(() => ({
	sectionName: 'mobile fixed cta',
	currentPage: route.fullPath,
	tagExclusive: props.isExclusive,
	companyName: props.companyName,
	agentName: props.agent?.name,
	clickProperty: true
}));

// Pass the real values to the composable
const propertyId = computed(() => props.propertyId);
const commercialId = computed(() => props.commercialId);
const initialFavoriteState = computed(() => props.inUserWishlist ?? false);

// useToggleFavoriteSync is synchronous - no await needed for SSR performance
const {
	isFavorite,
	isValidData,
	handleToggleFavorite: handleToggle
} = useToggleFavoriteSync(
	propertyId,
	commercialId,
	eventData,
	'mobile fixed cta',
	initialFavoriteState
);

// Wrapper function for error handling
async function handleToggleFavorite() {
	const result = await handleToggle();
	if (result?.error) {
		logger.error(
			{
				propertyId: props.propertyId,
				commercialId: props.commercialId,
				component: 'FixedFooterContact',
				section: 'mobile fixed cta',
				agentName: props.agent?.name,
				companyName: props.companyName,
				error: result.error
			},
			'[Favorite Toggle Error] Failed to toggle property favorite state in mobile footer'
		);

		// TODO: show error toast to user
	}
	return result;
}

// Animation gates
const isAnimationReady = ref(false);
const isReadyForFavorite = computed(() => isAnimationReady.value);

const { state, isAnimatingButton, canPulse } = useFavoriteEntranceAnimation({
	isReady: isReadyForFavorite,
	isFavorite: isFavorite
});

// Pulses (stars)
const {
	isMainPulsing: isMainStarPulsing,
	isBackgroundPulsing: isBackgroundStarPulsing
} = useFavoriteStarPulse({
	isEnabled: canPulse,
	isSelected: isFavorite
});

let readyTimeoutId: ReturnType<typeof setTimeout> | undefined;

onMounted(() => {
	readyTimeoutId = setTimeout(() => {
		isAnimationReady.value = true;
	}, ANIMATION_CONSTANTS.READY_DELAY_MS);
});

onUnmounted(() => {
	if (readyTimeoutId) {
		clearTimeout(readyTimeoutId);
	}
});

// Static CSS classes for animations
const transitionClasses = {
	enter: 'transition-transform [transition-duration:400ms] ease-out',
	leave: 'transition-transform [transition-duration:300ms] ease-in',
	spacer:
		'h-11 shrink-0 transition-[width] ease-out [transition-duration:1400ms]'
};

const spacerOpen = computed(
	() =>
		state.value === FavoriteEntranceState.ENTRANCE ||
		state.value === FavoriteEntranceState.READY ||
		state.value === FavoriteEntranceState.STATIC
);
</script>

<template>
	<ClientOnly>
		<div class="fixed inset-x-0 bottom-0 z-40 w-full lg:hidden">
			<Transition
				:enter-active-class="transitionClasses.enter"
				enter-from-class="translate-y-full"
				enter-to-class="translate-y-0"
				:leave-active-class="transitionClasses.leave"
				leave-from-class="translate-y-0"
				leave-to-class="translate-y-full"
			>
				<div
					v-if="isExclusive"
					class="relative z-10 flex w-full justify-center"
				>
					<div class="w-full max-w-full">
						<div
							class="flex items-center justify-center rounded-[2px] bg-beige px-1.5 py-1 text-center text-xs font-medium leading-[150%] text-primary"
						>
							<span class="mr-2 size-2.5">
								<GraphicsTriangle />
							</span>
							<span>Exclusivo</span>
							<span class="font-semibold">&nbsp;PilarHomes</span>
						</div>
					</div>
				</div>
			</Transition>

			<div
				:class="
					cn(
						'flex py-2 px-4 flex-col items-start gap-3 w-full max-w-full border-t border-white-dark-2 bg-white relative z-20',
						{ 'shadow-[0px_1px_3px_1px_rgba(0,0,0,0.15)]': !isExclusive }
					)
				"
			>
				<div class="flex w-full items-center gap-3 pr-0 sm:justify-between">
					<div class="w-12 shrink-0 sm:w-auto">
						<BrokerAvatarInfo
							:broker-src="agent?.profilePicture?.croppedUrl"
							:company-src="companyLogo"
							:broker-name="agent?.name"
							:show-company-avatar="true"
							avatar-class="size-8"
							sizes="linear"
							:info-classes="{
								wrapper: 'hidden sm:block',
								brokerName:
									'font-medium text-base leading-[13px] pt-[3px] text-primary pl-9',
								companyName:
									'font-normal text-xs leading-[1.5] text-primary-light-2 pl-9'
							}"
						>
							<template #broker>
								{{ agent?.name }}
							</template>
							<template #company> Corretor na {{ companyName }} </template>
						</BrokerAvatarInfo>
					</div>

					<div
						class="relative flex flex-1 items-center gap-0 overflow-hidden sm:justify-end"
					>
						<Button
							variant="primary"
							:is-client-only="false"
							:is-skeleton-loading="false"
							skeleton-classes="flex-1 sm:flex-initial"
							class="duration-[2s] flex h-11 items-center justify-center gap-2 rounded-[2px] bg-primary px-4 py-2 text-sm font-medium leading-5 text-white transition-all ease-out sm:flex-initial"
							@click="emit('clickFixedFooterContact')"
						>
							<IconMaterialSymbolsLightChatBubbleOutlineSharp
								class="size-5 text-beige"
							/>
							{{ ctaText }}
						</Button>

						<template v-if="isValidData">
							<div
								:class="[
									state === FavoriteEntranceState.ENTRANCE
										? transitionClasses.spacer
										: '',
									spacerOpen ? 'w-14 sm:w-[136px]' : 'w-0 sm:w-0'
								]"
								aria-hidden="true"
							/>

							<FavoriteFooterButton
								:is-ready="isReadyForFavorite"
								:is-selected="isFavorite"
								:is-animating="isAnimatingButton"
								:is-main-pulsing="isMainStarPulsing"
								:is-background-pulsing="isBackgroundStarPulsing"
								@toggle="handleToggleFavorite"
							/>
						</template>
					</div>
				</div>
			</div>
		</div>
	</ClientOnly>
</template>
